import React from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useGenerateInviteMutation, useGetMyInvitesQuery } from "../../services/rtkApi/invitesApi";

const Container = styled.section`
  width: 100%;
  border: 2px solid #273428;
  border-radius: 10px;
  padding: 16px;
  margin-top: 12px;
  background: #e5ffdf;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: #222;
`;

const Quota = styled.span`
  font-size: 14px;
  color: #2d442f;
`;

const GenerateButton = styled.button`
  border: 1px solid #223122;
  background: #2d6a3f;
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const List = styled.ul`
  margin: 14px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled.li`
  border: 1px solid #c8dcc4;
  border-radius: 8px;
  padding: 10px;
  background: #f5fff3;
`;

const CodeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Code = styled.code`
  font-size: 14px;
  background: #edf3ec;
  border-radius: 4px;
  padding: 2px 6px;
`;

const TinyButton = styled.button`
  border: 1px solid #344835;
  background: #fff;
  color: #2b3a2c;
  padding: 4px 8px;
  border-radius: 5px;
  cursor: pointer;
`;

const Meta = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #425443;
`;

const Status = styled.span<{ $status: string }>`
  font-size: 12px;
  border-radius: 12px;
  padding: 2px 8px;
  background: ${({ $status }) =>
    $status === "active"
      ? "#d7f5dc"
      : $status === "used"
        ? "#dbecff"
        : $status === "expired"
          ? "#ffe8c9"
          : "#ececec"};
  color: #243326;
  text-transform: capitalize;
`;

const Empty = styled.p`
  margin: 12px 0 0;
  color: #4b5f4d;
`;

const formatDate = (value?: string | null): string => {
  if (!value) {
    return "n/a";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "n/a";
  }
  return date.toLocaleString();
};

const InvitePanel: React.FC = () => {
  const { data, isLoading, isFetching } = useGetMyInvitesQuery();
  const [generateInvite, { isLoading: isGenerating }] = useGenerateInviteMutation();

  const remainingQuota = data?.remainingQuota ?? 0;
  const invites = data?.invites ?? [];

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("invite code copied", { position: "bottom-left" });
    } catch {
      toast.error("could not copy invite code", { position: "bottom-right" });
    }
  };

  const handleGenerateInvite = async () => {
    try {
      const result = await generateInvite(undefined).unwrap();
      toast.success(`new invite: ${result.code}`, { position: "bottom-left" });
      await handleCopy(result.code);
    } catch (error: any) {
      toast.error(error?.data?.error || "failed to generate invite", { position: "bottom-right" });
    }
  };

  return (
    <Container>
      <HeaderRow>
        <div>
          <SectionTitle>Invites</SectionTitle>
          <Quota>{remainingQuota} invites remaining</Quota>
        </div>
        <GenerateButton
          type="button"
          onClick={handleGenerateInvite}
          disabled={isGenerating || isLoading || isFetching || remainingQuota <= 0}
        >
          {isGenerating ? "Generating..." : "Generate Invite"}
        </GenerateButton>
      </HeaderRow>

      {isLoading ? <Empty>Loading invites...</Empty> : null}

      {!isLoading && invites.length === 0 ? <Empty>You have not generated invites yet.</Empty> : null}

      {invites.length > 0 ? (
        <List>
          {invites.map((invite) => (
            <Item key={invite._id}>
              <CodeRow>
                <Code>{invite.code}</Code>
                <TinyButton type="button" onClick={() => handleCopy(invite.code)}>
                  Copy
                </TinyButton>
                <Status $status={invite.status}>{invite.status}</Status>
              </CodeRow>
              <Meta>
                created: {formatDate(invite.createdAt)} | expires: {formatDate(invite.expiresAt)} | used by:{" "}
                {invite.usedBy || "n/a"}
              </Meta>
            </Item>
          ))}
        </List>
      ) : null}
    </Container>
  );
};

export default InvitePanel;
